/**
 * usePostMessage Hook - Post Messages to Discussion Forum
 *
 * This React hook provides a convenient interface for posting messages to
 * the discussion forum with proper authentication, error handling, and
 * integration with TripContext.
 *
 * Features:
 * - Automatic user context (senderId, senderName, avatar)
 * - effectiveUserId integration for shared trips
 * - Validation and error handling
 * - Loading states for UI feedback
 * - Integration with forumService
 *
 * Usage:
 * const { postMessage, posting, error } = usePostMessage();
 *
 * const handleSend = async () => {
 *   const messageId = await postMessage('Hello, team!', {
 *     threadId: 'general',
 *     someMetadata: 'value'
 *   });
 * };
 */

import { useState, useCallback } from 'react';
import { useTrip, useAuth } from '@trip-planner-monorepo/shared';
import { forumService } from '../services/forumService';

/**
 * Custom hook for posting messages
 *
 * @returns {Object} Hook state and methods
 * @returns {Function} returns.postMessage - Function to post a message
 * @returns {boolean} returns.posting - Whether a message is currently being posted
 * @returns {Object|null} returns.error - Error object if posting failed
 * @returns {Function} returns.clearError - Function to clear error state
 */
export const usePostMessage = () => {
  const { effectiveUserId, selectedTripId } = useTrip();
  const { currentUser } = useAuth();
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Post a message to the discussion forum
   *
   * @param {string} content - Message text content
   * @param {Object} metadata - Optional metadata
   * @param {string} [metadata.threadId='main'] - Thread identifier
   * @param {string} [metadata.type='text'] - Message type ('text', 'proposal', 'system')
   * @param {Object} [metadata.proposal] - Proposal data (if type is 'proposal')
   * @param {Object} [metadata.additionalMetadata] - Additional custom metadata
   * @returns {Promise<string|null>} The ID of the posted message, or null if failed
   * @throws {Error} If posting fails
   */
  const postMessage = useCallback(async (content, metadata = {}) => {
    // Validation
    if (!content || typeof content !== 'string') {
      const validationError = new Error('Message content is required and must be a string');
      setError(validationError);
      return null;
    }

    if (!content.trim()) {
      const validationError = new Error('Message content cannot be empty');
      setError(validationError);
      return null;
    }

    if (!currentUser) {
      const authError = new Error('User must be authenticated to post messages');
      setError(authError);
      return null;
    }

    if (!effectiveUserId || !selectedTripId) {
      const contextError = new Error('Trip context is required to post messages');
      setError(contextError);
      return null;
    }

    // Extract metadata with defaults
    const {
      threadId = 'main',
      type = 'text',
      proposal = null,
      additionalMetadata = {}
    } = metadata;

    setPosting(true);
    setError(null);

    try {
      // Helper: Get display name from user (email prefix with @... suffix)
      const getDisplayName = (user) => {
        console.log('[usePostMessage] Getting display name for user:', {
          uid: user?.uid,
          email: user?.email,
          displayName: user?.displayName
        });

        if (user?.displayName) {
          console.log('[usePostMessage] Using displayName:', user.displayName);
          return user.displayName;
        }
        if (user?.email) {
          const prefix = user.email.split('@')[0];
          const result = `${prefix}@...`;
          console.log('[usePostMessage] Using email prefix:', result);
          return result;
        }
        // Fallback: Use first 6 chars of UID if email not available
        if (user?.uid) {
          const result = `user-${user.uid.substring(0, 6)}@...`;
          console.warn('[usePostMessage] User has no email, using UID prefix:', result);
          return result;
        }
        console.error('[usePostMessage] No user info available, returning Anonymous');
        return 'Anonymous';
      };

      const senderName = getDisplayName(currentUser);
      console.log('[usePostMessage] Final senderName:', senderName);

      // Build message data with user context
      const messageData = {
        content: content.trim(), // Field name must match forumService expectation
        senderId: currentUser.uid, // Actual user ID (for event tracking and permissions)
        senderName: senderName,
        senderAvatar: currentUser.photoURL || null,
        effectiveUserId: effectiveUserId, // Trip owner for shared trips
        type: type,
        proposal: proposal,
        metadata: {
          threadId: threadId,
          ...additionalMetadata
        }
      };

      // Check if message mentions AI Agent for enhanced logging
      const mentionsAI = content.toLowerCase().includes('@ai_agent');

      if (mentionsAI) {
        console.log('═══════════════════════════════════════════════════════════');
        console.log('🚀 SENDING MESSAGE TO AI AGENT');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('📝 User Message:', content);
        console.log('👤 Sender:', senderName, `(${currentUser.uid})`);
        console.log('🆔 Trip ID:', selectedTripId);
        console.log('📍 Page Context:', additionalMetadata?.pageContext || 'None');
        console.log('⏰ Timestamp:', new Date().toLocaleString());
        console.log('═══════════════════════════════════════════════════════════');
      }

      // Post message via forumService
      // forumService delegates to discussionFirestoreService
      const messageId = await forumService.postMessage(
        selectedTripId,
        threadId,
        currentUser.uid,
        messageData
      );

      if (mentionsAI) {
        console.log('✅ Message sent to Firebase. Firestore trigger will invoke AI Agent.');
        console.log('⏳ Waiting for AI response...');
      } else {
        console.log(`[usePostMessage] Message posted successfully: ${messageId}`);
      }
      setPosting(false);
      return messageId;

    } catch (postError) {
      console.error('[usePostMessage] Error posting message:', postError);

      // Categorize error for better user feedback
      let errorMessage = postError.message;
      let errorCode = postError.code;

      if (postError.code === 'permission-denied') {
        errorMessage = 'You do not have permission to post to this discussion.';
        errorCode = 'PERMISSION_DENIED';
      } else if (postError.code === 'unavailable') {
        errorMessage = 'Network error. Please check your connection and try again.';
        errorCode = 'NETWORK_ERROR';
      } else if (postError.code === 'unauthenticated') {
        errorMessage = 'Authentication expired. Please log in again.';
        errorCode = 'AUTH_EXPIRED';
      }

      const enhancedError = {
        message: errorMessage,
        code: errorCode,
        details: postError
      };

      setError(enhancedError);
      setPosting(false);

      // Re-throw for caller to handle if needed
      throw enhancedError;
    }
  }, [effectiveUserId, selectedTripId, currentUser]);

  /**
   * Clear error state
   * Useful for dismissing error messages in UI
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    postMessage,
    posting,
    error,
    clearError
  };
};

/**
 * Implementation Notes:
 *
 * 1. User Context Integration:
 *    - senderId: currentUser.uid (actual user, for tracking and permissions)
 *    - effectiveUserId: Trip owner UID (for Firestore path)
 *    - senderName: Display name snapshot at write time
 *    - senderAvatar: User photo URL for UI rendering
 *
 * 2. effectiveUserId Pattern:
 *    - Shared trips: Messages stored under trip owner's path
 *    - Owned trips: Messages stored under current user's path
 *    - See CLAUDE.md "Shared Trip Data Access Pattern" for details
 *
 * 3. Message Validation:
 *    - Content must be non-empty string
 *    - User must be authenticated
 *    - Trip context must be available
 *    - Early validation prevents unnecessary API calls
 *
 * 4. Error Handling:
 *    - Permission errors: User lacks write access
 *    - Network errors: Offline or connectivity issues
 *    - Auth errors: Session expired or invalid
 *    - Validation errors: Invalid input data
 *
 * 5. Loading States:
 *    - posting: true while message is being written to Firestore
 *    - UI can disable input or show loading indicator
 *    - posting: false after success or error
 *
 * 6. Metadata Support:
 *    - threadId: Support for future threaded discussions
 *    - type: 'text' (default), 'proposal' (AI suggestions), 'system' (automated)
 *    - proposal: Structured data for AI-generated suggestions
 *    - additionalMetadata: Custom key-value pairs
 *
 * 7. forumService Integration:
 *    - Delegates to discussionFirestoreService for Firestore operations
 *    - May publish events for cross-package coordination
 *    - Handles business logic (permissions, validation)
 *
 * 8. Offline Support:
 *    - Firestore handles offline writes automatically
 *    - Message queued locally if offline
 *    - Auto-syncs when connection restored
 *    - timestamp will be null until server writes
 *
 * 9. Return Value:
 *    - Success: Returns messageId (string)
 *    - Validation failure: Returns null
 *    - Error: Throws enhanced error object
 *    - Caller can handle errors with try/catch or check return value
 *
 * 10. Future Enhancements:
 *     - Typing indicators (publish typing events)
 *     - Message editing (updateMessage method)
 *     - Message deletion (deleteMessage method)
 *     - Emoji reactions
 *     - File attachments
 */

/**
 * Example Usage:
 *
 * Basic text message:
 * ```javascript
 * const { postMessage, posting, error } = usePostMessage();
 *
 * const handleSend = async () => {
 *   try {
 *     const messageId = await postMessage('Hello, everyone!');
 *     console.log('Message sent:', messageId);
 *   } catch (err) {
 *     console.error('Failed to send:', err);
 *   }
 * };
 * ```
 *
 * AI proposal message:
 * ```javascript
 * const handleAIProposal = async () => {
 *   const messageId = await postMessage(
 *     'I suggest adding the Eiffel Tower to Day 3.',
 *     {
 *       type: 'proposal',
 *       proposal: {
 *         action: 'add_place',
 *         data: {
 *           placeId: 'place_123',
 *           name: 'Eiffel Tower',
 *           dayId: 'day_3'
 *         },
 *         status: 'pending'
 *       }
 *     }
 *   );
 * };
 * ```
 *
 * With custom metadata:
 * ```javascript
 * const messageId = await postMessage(
 *   'Meeting at 3pm?',
 *   {
 *     threadId: 'planning',
 *     additionalMetadata: {
 *       priority: 'high',
 *       category: 'coordination'
 *     }
 *   }
 * );
 * ```
 *
 * Error handling with UI feedback:
 * ```javascript
 * const { postMessage, posting, error, clearError } = usePostMessage();
 *
 * {error && (
 *   <Alert severity="error" onClose={clearError}>
 *     {error.message}
 *   </Alert>
 * )}
 *
 * <TextField
 *   disabled={posting}
 *   onKeyPress={(e) => {
 *     if (e.key === 'Enter' && !posting) {
 *       postMessage(e.target.value);
 *     }
 *   }}
 * />
 * ```
 */
